import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { DocumentDetailsApplicability } from "@src/appV2/redesign/Documents/Details/components/Applicability";
import { DocumentDetailsUploadStatus } from "@src/appV2/redesign/Documents/Details/components/UploadStatus";
import { DocumentUploadFooter } from "@src/appV2/redesign/Documents/Details/Footer";
import { DocumentPageScrollWrapper } from "@src/appV2/redesign/Documents/Details/ScrollWrapper";
import { DocumentDetailsStatusCard } from "@src/appV2/redesign/Documents/Details/StatusCard";
import { useDocumentDetailsActions } from "@src/appV2/redesign/Documents/Details/useDocumentDetailsActions";
import { useRequirementDetailsContext } from "@src/appV2/redesign/Documents/Details/useRequirementDetailsContext";
import { DOCUMENT_TB_TEST_GUIDED_UPLOAD_FULL_PATH } from "@src/appV2/redesign/Documents/paths";
import { useCallback, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { generateScheduleTbTestPath, SCHEDULE_TB_TEST_SOURCE } from "./constants";
import { TbTestGuidedUploadInstructions } from "./Instructions";

export function DocumentDetailsTbTestGuidedUploadPage() {
  const { requirement: workerRequirement } = useRequirementDetailsContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { requirementId, status, requirement } = workerRequirement;
  const { name } = requirement?.attributes ?? {};

  const { deleteDocument } = useDocumentDetailsActions();
  const history = useHistory();

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_TB_TEST_GUIDED_UPLOAD_PAGE, {
    requirementId,
    status,
  });

  const handleBeginUpload = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.TB_TEST_GUIDED_UPLOAD_STARTED, {
      requirementId,
    });
    history.replace(generatePath(DOCUMENT_TB_TEST_GUIDED_UPLOAD_FULL_PATH, { requirementId }));
  }, [requirementId, history]);

  const handleScheduleTbTest = useCallback(() => {
    history.push(generateScheduleTbTestPath(requirementId, SCHEDULE_TB_TEST_SOURCE.DETAILS_PAGE));
  }, [requirementId, history]);

  return (
    <PageWrapper variant="quaternary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title={name ?? ""}
        headerVariant="quaternary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef} spacing={9}>
        <DocumentDetailsUploadStatus />
        <DocumentDetailsStatusCard />
        <TbTestGuidedUploadInstructions />
        <DocumentDetailsApplicability />
      </DocumentPageScrollWrapper>

      <DocumentUploadFooter
        replaceUploadButtonText="Upload Replacement"
        status={status}
        resubmissionDisabledFor={requirement?.attributes?.resubmissionDisabledFor}
        uploadButtonText="Begin Upload"
        onDeleteButtonClick={deleteDocument}
        onUploadButtonClick={handleBeginUpload}
      >
        <TextButton
          size="large"
          sx={{ marginTop: 4, display: "flex", marginX: "auto" }}
          onClick={handleScheduleTbTest}
        >
          Schedule a TB Test
        </TextButton>
      </DocumentUploadFooter>
    </PageWrapper>
  );
}
